import { useState, useEffect, useRef, useCallback } from 'react'
import {
  acquireDprSectionLease,
  releaseDprSectionLease,
  getDprActiveLeases,
} from './_requests'

export interface DprSectionLease {
  id: number
  reportId: number
  sectionName: string
  userId: number
  userName: string
  userRank?: string
  expiresAt: string
  isCurrentUsersLease: boolean
}

export interface DprRealtimeEvent {
  eventType: 'SECTION_UPDATED' | 'LEASE_ACQUIRED' | 'LEASE_RELEASED' | 'STATUS_CHANGED' | 'HEARTBEAT'
  reportId: number
  sectionName?: string
  userId?: number
  userName?: string
  userRank?: string
  version?: number
  payload?: any
  timestamp: string
}

const API_URL = process.env.REACT_APP_API_URL

export const useDprRealtime = (
  reportId?: number,
  onRemoteSectionUpdate?: (sectionName: string, payload: any, updatedBy: string) => void
) => {
  const [isConnected, setIsConnected] = useState(false)
  const [leases, setLeases] = useState<DprSectionLease[]>([])
  const [activeEditingSection, setActiveEditingSection] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null)

  // ── Fetch active leases ──
  const refreshLeases = useCallback(async () => {
    if (!reportId) return
    try {
      const active = await getDprActiveLeases(reportId)
      setLeases(active)
    } catch (err) {
      console.error('Failed to fetch DPR active leases:', err)
    }
  }, [reportId])

  // ── Acquire or renew soft lease ──
  const acquireLease = useCallback(
    async (sectionName: string): Promise<boolean> => {
      if (!reportId) return true
      try {
        const lease = await acquireDprSectionLease(reportId, sectionName)
        setActiveEditingSection(sectionName)
        setLeases((prev) => [
          ...prev.filter((l) => l.sectionName !== sectionName),
          { ...lease, isCurrentUsersLease: true },
        ])
        return true
      } catch (err: any) {
        console.warn(`Could not acquire lease for section ${sectionName}:`, err)
        return false
      }
    },
    [reportId]
  )

  // ── Release soft lease ──
  const releaseLease = useCallback(
    async (sectionName: string) => {
      if (!reportId) return
      try {
        await releaseDprSectionLease(reportId, sectionName)
        if (activeEditingSection === sectionName) {
          setActiveEditingSection(null)
        }
        setLeases((prev) => prev.filter((l) => !(l.sectionName === sectionName && l.isCurrentUsersLease)))
      } catch (err) {
        console.error(`Failed to release lease for section ${sectionName}:`, err)
      }
    },
    [reportId, activeEditingSection]
  )

  // ── Periodic heartbeat for active editing section (every 60s) ──
  useEffect(() => {
    if (!reportId || !activeEditingSection) {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current)
      return
    }

    heartbeatTimerRef.current = setInterval(() => {
      acquireLease(activeEditingSection)
    }, 60000)

    return () => {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current)
    }
  }, [reportId, activeEditingSection, acquireLease])

  // ── Connect SSE Stream ──
  useEffect(() => {
    if (!reportId) {
      setIsConnected(false)
      return
    }

    refreshLeases()

    const streamUrl = `${API_URL}/dpr/${reportId}/events`
    const es = new EventSource(streamUrl, { withCredentials: true })
    eventSourceRef.current = es

    es.onopen = () => {
      setIsConnected(true)
    }

    es.addEventListener('SECTION_UPDATED', (e: MessageEvent) => {
      try {
        const data: DprRealtimeEvent = JSON.parse(e.data)
        if (data && data.sectionName && data.payload) {
          onRemoteSectionUpdate?.(data.sectionName, data.payload, data.userName || 'Another user')
        }
      } catch (err) {
        console.error('Error handling SECTION_UPDATED event:', err)
      }
    })

    es.addEventListener('LEASE_ACQUIRED', (e: MessageEvent) => {
      try {
        const data: DprRealtimeEvent = JSON.parse(e.data)
        if (data && data.payload) {
          setLeases((prev) => [
            ...prev.filter((l) => l.sectionName !== data.sectionName),
            data.payload,
          ])
        }
      } catch (err) {
        console.error('Error handling LEASE_ACQUIRED event:', err)
      }
    })

    es.addEventListener('LEASE_RELEASED', (e: MessageEvent) => {
      try {
        const data: DprRealtimeEvent = JSON.parse(e.data)
        if (data && data.sectionName) {
          setLeases((prev) => prev.filter((l) => l.sectionName !== data.sectionName))
        }
      } catch (err) {
        console.error('Error handling LEASE_RELEASED event:', err)
      }
    })

    es.onerror = () => {
      setIsConnected(false)
    }

    return () => {
      es.close()
      eventSourceRef.current = null
      setIsConnected(false)
    }
  }, [reportId, onRemoteSectionUpdate, refreshLeases])

  return {
    isConnected,
    leases,
    acquireLease,
    releaseLease,
    refreshLeases,
  }
}
